import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reelService } from '../services/api';
import useIsSmallScreen from '../hooks/useIsSmallScreen';
import '../styles/ReelsView.css';

const PAGE_SIZE = 20;

const ReelsView = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  /* eslint-disable no-unused-vars */
  const isSmallScreen = useIsSmallScreen();
  /* eslint-enable no-unused-vars */

  // Refs
  const videoRefs = useRef([]);
  const containerRef = useRef(null);
  const scrollTimeout = useRef(null);
  const observerRef = useRef(null);
  const reelRefs = useRef([]);
  const loadingPageRef = useRef(null);
  const scrollAnimationFrame = useRef(null);

  // State
  const [reelList, setReelList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const MAX_LENGTH = 100;

  useEffect(() => {
    loadReels(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // Force play first video when reels are loaded
  useEffect(() => {
    console.log('Reels loaded, total count:', reelList.length);
    if (reelList.length > 0 && currentIndex === 0) {
      const firstVideo = videoRefs.current[0];
      if (firstVideo) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        
        const playVideo = () => {
          firstVideo.muted = isMuted;
          firstVideo.play().catch((err) => {
            console.log('Initial autoplay failed:', err);
          });
        };

        // Delay only for iOS
        if (isIOS) {
          setTimeout(playVideo, 100);
        } else {
          playVideo();
        }
      }
    }
  }, [reelList.length, currentIndex]);

  const loadReels = async (page = 1) => {
    if (isLoading || loadingPageRef.current === page) return;

    loadingPageRef.current = page;
    setIsLoading(true);

    try {
      if (categoryId) {
        const offset = (page - 1) * PAGE_SIZE;
        const res = await reelService.getReelsByCategory(categoryId, PAGE_SIZE, offset);

        // Handle different possible API response structures
        let newItems = [];
        if (Array.isArray(res.data)) {
          newItems = res.data;
        } else if (res.data && Array.isArray(res.data.reels)) {
          newItems = res.data.reels;
        } else if (res.data && Array.isArray(res.data.data)) {
          newItems = res.data.data;
        }

        if (page === 1) {
          setReelList(newItems);
        } else {
          setReelList((prev) => {
            const existingIds = new Set(prev.map((p) => p.id));
            const filtered = newItems.filter((n) => !existingIds.has(n.id));
            return [...prev, ...filtered];
          });
        }

        setCurrentPage(page);

        if (!newItems || newItems.length < PAGE_SIZE) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      }
    } catch (error) {
      console.error('Error loading reels:', error);
      setHasMore(false);
    } finally {
      loadingPageRef.current = null;
      setIsLoading(false);
    }
  };

  // Intersection Observer for detecting current reel
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const index = reelRefs.current.indexOf(entry.target);
            if (index !== -1 && index !== currentIndex) {
              setCurrentIndex(index);
            }
          }
        });
      },
      {
        root: containerRef.current,
        threshold: [0.3, 0.6, 0.9],
      }
    );

    observerRef.current = observer;

    reelRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [reelList, currentIndex]);

  // Autoplay logic with iOS Safari support
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentIndex) {
          // For iOS, ensure video is inline and muted first
          if (isIOS) {
            video.muted = true;
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            video.setAttribute('webkit-playsinline', '');
          }

          // Try to play with slight delay to ensure video is ready
          const attemptPlay = async () => {
            try {
              video.muted = isMuted;
              video.load(); // Reload video to ensure it's ready
              await video.play();
            } catch (err) {
              console.log('Autoplay prevented, trying again:', err);
              // On iOS, retry after a short delay
              if (isIOS) {
                setTimeout(async () => {
                  try {
                    video.muted = isMuted;
                    video.load();
                    await video.play();
                  } catch (e) {
                    console.log('Second play attempt failed:', e);
                  }
                }, 100);
              } else {
                // Immediate retry for non-iOS
                try {
                  video.muted = isMuted;
                  video.load();
                  await video.play();
                } catch (e) {
                  console.log('Second play attempt failed:', e);
                }
              }
            }
          };
          
          if (video.readyState >= 2) {
            attemptPlay();
          } else {
            video.addEventListener('loadeddata', attemptPlay, { once: true });
          }
        } else if (index === currentIndex + 1) {
          // Preload next video for smooth transition
          video.load();
        } else {
          video.pause();
          video.currentTime = 0; // Reset
        }
      }
    });

    // View count tracking for current reel
    if (reelList[currentIndex]) {
      // Optional: call view count API if needed
      // reelService.getReelById(reelList[currentIndex].id).catch(e => console.error(e));
    }
  }, [currentIndex, reelList]);

  const navigateToReel = React.useCallback((index) => {
    if (index >= 0 && index < reelList.length) {
      setCurrentIndex(index);
      if (containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        containerRef.current.scrollTo({
          top: index * containerHeight,
          behavior: 'smooth',
        });
      }
    } else if (index >= reelList.length && hasMore && !isLoading) {
      loadReels(currentPage + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reelList.length, hasMore, isLoading, currentPage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigateToReel(currentIndex - 1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        navigateToReel(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, navigateToReel]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    if (scrollAnimationFrame.current) {
      cancelAnimationFrame(scrollAnimationFrame.current);
    }

    scrollAnimationFrame.current = requestAnimationFrame(() => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 200;

      if (isNearBottom && hasMore && !isLoading) {
        loadReels(currentPage + 1);
      }
    });

    // Fallback sync of currentIndex if scrolling manually
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      const containerHeight = container.clientHeight;
      const newIndex = Math.round(container.scrollTop / containerHeight);
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < reelList.length) {
        setCurrentIndex(newIndex);
      }
    }, 150);
  };

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
    videoRefs.current.forEach(v => {
      if (v) v.muted = !isMuted;
    });
  };

  // Allow click to pause/play current
  const handleVideoClick = (index) => {
    const video = videoRefs.current[index];
    if (video) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  };

  if (!reelList.length && !isLoading) {
    return (
      <div className="reels-view-loading">
        <div className="text-white">No reels found here.</div>
        <button className="back-btn-overlay" onClick={() => navigate('/')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="reels-view-container bg-black">
      {/* Back Button */}
      <button className="back-btn-overlay" onClick={() => navigate('/')}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Reels Container */}
      <div
        ref={containerRef}
        className="reels-scroll-container"
        onScroll={handleScroll}
      >
        {reelList.map((reel, index) => (
          <div
            key={`${reel.id}-${index}`}
            ref={(el) => (reelRefs.current[index] = el)}
            className="reel-section"
          >
            <div className="reel-wrapper">
              <video
                ref={(el) => (videoRefs.current[index] = el)}
                src={encodeURI(reel.video_url || reel.medias?.[0]?.url)}
                className="reel-video-element"
                loop
                muted={isMuted}
                playsInline
                webkit-playsinline="true"
                x5-playsinline="true"
                preload="metadata"
                onClick={() => handleVideoClick(index)}
              />

              {/* Mute Toggle - Moved to top right */}
              <button onClick={toggleMute} className="mute-toggle-btn">
                {isMuted ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" /></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" /></svg>
                )}
              </button>

              {/* Info Overlay */}
              <div className="reel-overlay">
                <div className="reel-content-wrapper">
                  {/* Category Badge */}
                  <div className="category-badge">
                    <img 
                      src={reel.category_image ? encodeURI(reel.category_image) : '/category-placeholder.png'} 
                      alt={reel.category_name || 'Category'}
                      className="category-avatar"
                    />
                  </div>

                  {/* Title and Description */}
                  <div className="reel-details">
                    <h3 className="reel-title">{reel.title}</h3>
                    <div className="reel-desc">
                      {isExpanded ? reel.description : reel.description?.slice(0, MAX_LENGTH)}
                      {reel.description?.length > MAX_LENGTH && (
                        <span className="read-more" onClick={() => setIsExpanded(!isExpanded)}>
                          {isExpanded ? ' Read less' : '... Read more'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {isLoading && reelList.length > 0 && (
          <div className="loading-indicator">Loading...</div>
        )}
      </div>
    </div>
  );
};

export default ReelsView;
